/**
 * Custom hook for invoice calculation logic
 * Handles API calls to calculate invoice totals, VAT, pension, holiday pay, etc.
 * Includes race condition prevention for multiple rapid API calls
 */

import { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from './useRedux';
import { calculateInvoice } from '../features/invoice/invoice.slice';
import { transformDeductionToWorkingHour } from '../utils/deduction.utils';

// Wait this long after the last change before hitting /total-calculation.
// Collapses rapid edits (e.g. typing a price) into a single API call.
const CALCULATION_DEBOUNCE_MS = 500;

interface UseInvoiceCalculationProps {
  serviceTableDataByFreelancer: { [key: string]: any };
  currency: string;
  deductionType: string;
  vat: number;
  hourlyPrice: string;
  selectedFreelancersForInvoice: any[];
  pensionDataByFreelancer: { [key: string]: any };
  holidayDataByFreelancer: { [key: string]: any };
  deductionsByFreelancer: { [key: string]: any[] };
  watch: any;
  calculatedAmounts: any;
  preferences: any;
  customerState: any;
  globalData: any;
  copiedInvoiceData: any;
  removedCopiedWorkingHours: Set<number>;
  isInitialMount: boolean;
  createdDate?: string;
  invoiceNumber?: number; // Invoice number from URL for edit mode
}

export const useInvoiceCalculation = ({
  serviceTableDataByFreelancer,
  currency,
  deductionType,
  vat,
  hourlyPrice,
  selectedFreelancersForInvoice,
  pensionDataByFreelancer,
  holidayDataByFreelancer,
  deductionsByFreelancer,
  watch,
  calculatedAmounts,
  preferences,
  customerState,
  globalData,
  copiedInvoiceData,
  removedCopiedWorkingHours,
  isInitialMount,
  createdDate,
  invoiceNumber = 0 // Default to 0 if not provided
}: UseInvoiceCalculationProps) => {
  const dispatch = useAppDispatch();
  
  // State for calculated amounts and loading status
  const [calculatedSubAmount, setCalculatedSubAmount] = useState<any>(null);
  const [isCalculateInvoiceLoading, setIsCalculateInvoiceLoading] = useState(false);
  
  // Refs to track previous values for change detection
  const prevServiceDataRef = useRef<string>('{}');
  const prevCurrencyRef = useRef<string>(currency);
  const prevDeductionTypeRef = useRef<string>(deductionType);
  const prevVatRef = useRef<number>(vat);
  const prevHourlyPriceRef = useRef<string>(hourlyPrice || '');
  const prevPensionDataRef = useRef<string>(JSON.stringify(pensionDataByFreelancer));
  const prevHolidayDataRef = useRef<string>(JSON.stringify(holidayDataByFreelancer));
  const prevDeductionsDataRef = useRef<string>(JSON.stringify(deductionsByFreelancer));
  const prevRemovedCopiedWorkingHoursRef = useRef<string>(JSON.stringify(Array.from(removedCopiedWorkingHours)));
  
  // Request ID counter for race condition prevention
  // Ensures that only the latest API response updates the state
  const latestRequestIdRef = useRef<number>(0);

  // Debounce timer + handle to the currently in-flight request (for cancellation).
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRequestRef = useRef<{ abort: () => void } | null>(null);

  // On unmount: cancel any pending debounce and abort any in-flight calculation.
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (inFlightRequestRef.current) {
        inFlightRequestRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount) {
      return;
    }

    // Get the active freelancer ID (either 'default' or specific freelancer's userId)
    const isCollectiveInvoice = selectedFreelancersForInvoice && selectedFreelancersForInvoice.length > 0;
    
    // Get all freelancer IDs
    let freelancerIds = isCollectiveInvoice
      ? selectedFreelancersForInvoice.map((f: any) => (f.userId || f.id).toString())
      : ['default'];
    
    // If we have timesheet data, filter to only include freelancers with actual data
    if (copiedInvoiceData?.workingHours && copiedInvoiceData.workingHours.length > 0) {
      // Get unique userIds from timesheet working hours
      const timesheetUserIds = new Set(
        copiedInvoiceData.workingHours
          .filter((wh: any) => wh.userId)
          .map((wh: any) => wh.userId.toString())
      );
      
      // Only include freelancers that have data in serviceTableDataByFreelancer OR in timesheet
      freelancerIds = freelancerIds.filter((fId: string) => {
        const hasServiceData = serviceTableDataByFreelancer[fId]?.workingHours?.length > 0;
        const hasTimesheetData = timesheetUserIds.has(fId) || fId === 'default';
        const hasDeductions = deductionsByFreelancer[fId]?.length > 0;
        return hasServiceData || hasTimesheetData || hasDeductions;
      });
      
      // If no freelancers have data, default to 'default'
      if (freelancerIds.length === 0) {
        freelancerIds = ['default'];
      }
    }

    // Check if there's any meaningful data to calculate
    const hasWorkingHours = Object.values(serviceTableDataByFreelancer).some(
      (data: any) => data && data.workingHours && data.workingHours.length > 0
    );
    
    const hasDeductions = Object.values(deductionsByFreelancer).some(
      (deductions: any) => deductions && deductions.length > 0
    );
    
    // Check if we had data before but now it's empty (all rows removed)
    const hadWorkingHoursBefore = prevServiceDataRef.current && prevServiceDataRef.current !== '{}';
    const isDataCleared = hadWorkingHoursBefore && !hasWorkingHours && !hasDeductions;
    
    // If all working hours and deductions are removed, clear the calculated amounts immediately
    if (!hasWorkingHours && !hasDeductions) {
      // Cancel any pending/in-flight calculation so a late response can't override the cleared totals
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      if (inFlightRequestRef.current) {
        inFlightRequestRef.current.abort();
        inFlightRequestRef.current = null;
      }
      latestRequestIdRef.current += 1; // invalidate any request already in progress

      if (calculatedSubAmount !== null) {
        setCalculatedSubAmount(null);
      }
      
      // Reset the ref
      if (hadWorkingHoursBefore) {
        prevServiceDataRef.current = '{}';
      }
      
      // Don't proceed with API call if there's no data
      if (!isDataCleared) {
        return;
      }
    }

    // Create a stable string representation of service data for comparison
    // Include ALL working hours details to ensure proper change detection
    const currentServiceDataStr = JSON.stringify(
      Object.keys(serviceTableDataByFreelancer).reduce((acc: any, key) => {
        const data = serviceTableDataByFreelancer[key];
        if (data && data.workingHours.length > 0) {
          acc[key] = {
            totalAmount: data.totalAmount,
            hourlyWagesAmount: data.hourlyWagesAmount,
            workingHoursCount: data.workingHours.length,
            // Track ALL working hours data including service descriptions
            // IMPORTANT: Exclude dateId from comparison as it contains UUID that changes every render
            workingHoursData: data.workingHours.map((wh: any) => ({
              workingDate: wh.workingDate,
              number: wh.number,
              unit: wh.unit,
              amountPerUnit: wh.amountPerUnit,
              amount: wh.amount,
              articleNumber: wh.articleNumber,
              workingDescription: wh.workingDescription,
              CustomWorkingDescription: wh.CustomWorkingDescription,
              workingHour: wh.workingHour
            }))
          };
        }
        return acc;
      }, {})
    );

    // Create stable string representation of pension, holiday, and deductions data
    const currentPensionDataStr = JSON.stringify(pensionDataByFreelancer);
    const currentHolidayDataStr = JSON.stringify(holidayDataByFreelancer);
    const currentDeductionsDataStr = JSON.stringify(deductionsByFreelancer);
    const currentRemovedCopiedWorkingHoursStr = JSON.stringify(Array.from(removedCopiedWorkingHours));

    // Check if currency changed
    const currencyChanged = prevCurrencyRef.current !== currency;
    
    // Check if deduction type changed (ROT/RUT)
    const deductionTypeChanged = prevDeductionTypeRef.current !== deductionType;
    
    // Check if VAT changed
    const vatChanged = prevVatRef.current !== vat;
    
    // Check if hourly price changed
    const hourlyPriceChanged = prevHourlyPriceRef.current !== (hourlyPrice || '');
    
    // Check if service data changed (dates, pricing type, etc.)
    const serviceDataChanged = prevServiceDataRef.current !== currentServiceDataStr;

    // Check if pension or holiday data changed
    const pensionDataChanged = prevPensionDataRef.current !== currentPensionDataStr;
    const holidayDataChanged = prevHolidayDataRef.current !== currentHolidayDataStr;
    
    // Check if deductions data changed
    const deductionsDataChanged = prevDeductionsDataRef.current !== currentDeductionsDataStr;
    
    // Check if removed copied working hours changed (this is critical for allowing users to remove from copiedInvoiceData)
    const removedCopiedWorkingHoursChanged = prevRemovedCopiedWorkingHoursRef.current !== currentRemovedCopiedWorkingHoursStr;

    // Only proceed if something actually changed
    if (!currencyChanged && !deductionTypeChanged && !vatChanged && !hourlyPriceChanged && !serviceDataChanged && !pensionDataChanged && !holidayDataChanged && !deductionsDataChanged && !removedCopiedWorkingHoursChanged) {
      return;
    }

    // Update refs
    prevServiceDataRef.current = currentServiceDataStr;
    prevCurrencyRef.current = currency;
    prevDeductionTypeRef.current = deductionType;
    prevVatRef.current = vat;
    prevHourlyPriceRef.current = hourlyPrice || '';
    prevPensionDataRef.current = currentPensionDataStr;
    prevHolidayDataRef.current = currentHolidayDataStr;
    prevDeductionsDataRef.current = currentDeductionsDataStr;
    prevRemovedCopiedWorkingHoursRef.current = currentRemovedCopiedWorkingHoursStr;

    // Process ALL freelancers together (same as createInvoice)
    const processCalculation = async () => {
      // Calculate total amounts from ALL freelancers
      let totalAmountFromWorkingHours = 0;
      const allWorkingHours: any[] = [];
      const invoiceFreelancerArr: any[] = [];

      // Process each freelancer (using for...of to support async/await)
      for (const freelancerId of freelancerIds) {
        const serviceData = serviceTableDataByFreelancer[freelancerId] || { workingHours: [], totalAmount: 0 };
        const deductions = deductionsByFreelancer[freelancerId] || [];
        
        // Get actual userId
        const actualUserId = freelancerId === 'default' 
          ? (watch('userId') || 0)
          : parseInt(freelancerId);
        
        // Add service working hours with userId - preserve each working hour's VAT (important for deductions with their own VAT)
        const serviceWorkingHours = serviceData.workingHours.map((wh: any) => ({
          ...wh,
          userId: actualUserId,
          vat: wh.vat != null ? wh.vat : vat  // Preserve working hour's VAT (e.g., Travel Expenses: 6), fallback to global VAT
        }));
        allWorkingHours.push(...serviceWorkingHours);
        
        // Add copied working hours from copiedInvoiceData for this freelancer (non-service articles only)
        if (copiedInvoiceData?.workingHours) {
          const copiedWorkingHoursForFreelancer = copiedInvoiceData.workingHours.filter((wh: any, index: number) => {
            const isServiceArticle = ['92', '94', '95', '96', '97'].includes(wh.articleNumber);
            const isThisFreelancer = wh.userId?.toString() === actualUserId.toString();
            const isRemoved = removedCopiedWorkingHours.has(index);
            return !isServiceArticle && isThisFreelancer && !isRemoved;
          }).map((wh: any) => ({
            ...wh,
            vat: wh.vat != null ? wh.vat : vat  // Preserve copied working hour's VAT
          }));
          allWorkingHours.push(...copiedWorkingHoursForFreelancer);
        }
        
        // Add deduction working hours with userId
        for (const deduction of deductions) {
          try {
            const currencyRate = (globalData?.currencies?.find((item: any) => item.description === currency)?.globalValue || '1').toString();
            const workingHour = await transformDeductionToWorkingHour(
              deduction,
              vat,
              currencyRate
            );
            if (workingHour) {
              allWorkingHours.push({
                ...workingHour,
                userId: actualUserId
              });
            }
          } catch (error) {
            console.error('Error transforming deduction:', error, deduction);
          }
        }
        
        // Calculate freelancer amount from working hours
        const deductionAmounts = await Promise.all(
          deductions.map(async (d) => {
            const wh = await transformDeductionToWorkingHour(
              d, 
              vat, 
              (globalData?.currencies?.find((item: any) => item.description === currency)?.globalValue || '1').toString()
            );
            return wh ? wh.amount : 0;
          })
        );
        const freelancerAmount = [...serviceWorkingHours, ...deductionAmounts].reduce((sum: number, wh: any) => {
          return sum + (typeof wh === 'number' ? wh : (wh?.amount || 0));
        }, 0);
        
        totalAmountFromWorkingHours += freelancerAmount;
        
        // Get pension and holiday data for this freelancer
        const pensionData = pensionDataByFreelancer[freelancerId] || pensionDataByFreelancer['default'] || {};
        const holidayData = holidayDataByFreelancer[freelancerId] || holidayDataByFreelancer['default'] || {};
        
        // Always include freelancer in invoiceFreelancer array, even if they have no working hours
        // This ensures freelancers are not removed from the payload when all their hours are deleted
        invoiceFreelancerArr.push({
          userId: actualUserId,
          freelancerId: actualUserId,
          amount: freelancerAmount,
          pension: pensionData?.pension || false,
          pensionAmount: pensionData?.pensionAmount || 0,
          holidaySavings: holidayData?.holidaySavings || false,
          holidaySavingsFee: holidayData?.holidaySavingsFee || 0,
        });
      }
      
      // Determine pension and holiday savings
      const pensionToSend = invoiceFreelancerArr.some(f => f.pension);
      const holidaySavingsToSend = invoiceFreelancerArr.some(f => f.holidaySavings);
      const pensionAmountToSend = invoiceFreelancerArr.reduce((sum, f) => sum + (f.pensionAmount || 0), 0);
      const holidaySavingsFeeToSend = invoiceFreelancerArr.reduce((sum, f) => sum + (f.holidaySavingsFee || 0), 0);
      
      // Determine multipleFreelancer
      const multipleFreelancer = invoiceFreelancerArr.length > 1;
      
      // Remove duplicate workingHours by ID fields or key attributes
      // Keep first occurrence, remove all subsequent duplicates
      const uniqueWorkingHours = allWorkingHours.reduce((acc: any[], current: any) => {
        const isDuplicate = acc.some((item: any) => {
          // Priority 1: Check for duplicate by workingHourId (if exists and not 0)
          if (current.workingHourId && current.workingHourId !== 0) {
            return item.workingHourId && item.workingHourId === current.workingHourId;
          }
          
          // Priority 2: Check for duplicate by timeSheetWorkingHourId (if exists and not 0)
          if (current.timeSheetWorkingHourId && current.timeSheetWorkingHourId !== 0) {
            return item.timeSheetWorkingHourId && item.timeSheetWorkingHourId === current.timeSheetWorkingHourId;
          }
          
          // Priority 3: Check for duplicate by id field (if exists and not 0)
          if (current.id && current.id !== 0) {
            return item.id && item.id === current.id;
          }
          
          // Priority 4: Check by dateId (unique identifier generated for each working hour)
          if (current.dateId && current.dateId !== '' && item.dateId && item.dateId !== '') {
            return item.dateId === current.dateId;
          }
          
          // Priority 5: For new entries with no valid IDs, check by key fields
          // This prevents adding the same working hour multiple times
          if ((!current.workingHourId || current.workingHourId === 0) &&
              (!current.timeSheetWorkingHourId || current.timeSheetWorkingHourId === 0) &&
              (!current.id || current.id === 0) &&
              (!current.dateId || current.dateId === '')) {
            return item.workingDate === current.workingDate &&
                   item.workingHour === current.workingHour &&
                   item.amount === current.amount &&
                   item.articleNumber === current.articleNumber &&
                   item.userId === current.userId;
          }
          
          return false;
        });
        
        if (!isDuplicate) {
          acc.push(current);
        } else {
          console.log(`⚠️ Skipping duplicate working hour in invoice calculation:`, current);
        }
        
        return acc;
      }, []);
      
      // Remove duplicate invoiceFreelancer by userId (keep first occurrence)
      const uniqueInvoiceFreelancer = invoiceFreelancerArr.reduce((acc: any[], current: any) => {
        const isDuplicate = acc.some(item => item.userId && current.userId && item.userId === current.userId);
        if (!isDuplicate) {
          acc.push(current);
        }
        return acc;
      }, []);
      
      // Build the exact same payload as createInvoice uses
      const payload: any = {
        approvalInvoiceId: 0,
        rootDeductionAmount: calculatedAmounts?.rootDeductionAmount || 0,
        rutDeductionAmount: calculatedAmounts?.rutDeductionAmount || 0,
        invoiceId: 0,
        sendInvoice: 0, // 0 for calculation (not sending)
        vat: vat,
        pension: pensionToSend,
        amount: totalAmountFromWorkingHours || 0,
        isFixed: watch('isFixed') || false,
        currency: currency,
        brfNo: preferences?.brfOrgNumber || '',
        registerAssignment: false,
        showDates: preferences?.showDates === 'yes' || preferences?.showDates === true,
        house: preferences?.accommodationType === 'house',
        holidaySavings: holidaySavingsToSend,
        description: watch('otherDetails')?.customerNotes || watch('description') || '',
        taxValue: calculatedAmounts?.tax || 0,
        totalToPay: calculatedAmounts?.net || 0,
        rutDeduction: deductionType === 'rut',
        rootDeduction: deductionType === 'rot',
        file: null,
        expressPayout: watch('expressPayout') || false,
        workingHours: uniqueWorkingHours,
        email: customerState?.selectedCustomer?.email || '',
        apartment: preferences?.accommodationType === 'apartment',
        pensionFee: pensionToSend ? (calculatedAmounts?.pensionPercentage || 0) : 0,
        holidaySavingsFee: holidaySavingsToSend ? holidaySavingsFeeToSend : 0,
        tax: watch('incomeTax') || 0,
        rotApartmentNo: preferences?.apartmentHouseNo || '',
        pensionAmount: pensionAmountToSend,
        customerId: customerState?.selectedCustomer?.customerId || 0,
        expenditureForMaterialsAndToolsTaxFreeAmount: 0,
        holidaySavingsAmount: calculatedAmounts?.holidayPayAmount || 0,
        amountIncVat: Math.round(calculatedAmounts?.amountIncludingVat || 0),
        secondaryEmailAddress: customerState?.selectedCustomer?.secondaryEmailAddress || '',
        multipleFreelancer: multipleFreelancer,
        currencyRate: globalData?.currencies?.find((item: any) => item.description === currency)?.globalValue || '1',
        invoiceFreelancer: uniqueInvoiceFreelancer,
        locale: 'en-us',
        createdDate: createdDate || new Date().toISOString(),
        invoiceNumber: invoiceNumber // Add invoice number from URL
      };
      
      // Cancel any still in-flight calculation so only the latest request runs.
      // Frees the backend connection and stops superseded requests from timing out.
      if (inFlightRequestRef.current) {
        inFlightRequestRef.current.abort();
      }

      // Increment request ID and store it for this specific request
      latestRequestIdRef.current += 1;
      const currentRequestId = latestRequestIdRef.current;

      // Dispatch the calculation API and get the response
      try {
        setIsCalculateInvoiceLoading(true);
        const request = dispatch(calculateInvoice(payload));
        inFlightRequestRef.current = request; // keep handle so a newer change can cancel it
        const result = await request.unwrap();

        // Only update state if this is still the latest request
        // This prevents race conditions where an older API response arrives after a newer one
        if (currentRequestId === latestRequestIdRef.current) {
          setCalculatedSubAmount(result);
        }
      } catch (error: any) {
        // Ignore intentionally-cancelled (aborted) or superseded requests
        if (error?.name === 'AbortError' || error?.message === 'Aborted' || currentRequestId !== latestRequestIdRef.current) {
          return;
        }
        console.error('❌ Error calling calculateInvoice API:', error);
      } finally {
        // Only clear loading / release the handle if this is still the latest request
        if (currentRequestId === latestRequestIdRef.current) {
          setIsCalculateInvoiceLoading(false);
          inFlightRequestRef.current = null;
        }
      }
    };

    // Debounce the API call: collapse rapid successive changes into a single request
    // fired once the user pauses. A newer change clears the pending timer and reschedules.
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      processCalculation();
    }, CALCULATION_DEBOUNCE_MS);
  }, [
    serviceTableDataByFreelancer,
    currency,
    deductionType,
    vat,
    hourlyPrice,
    selectedFreelancersForInvoice,
    pensionDataByFreelancer,
    holidayDataByFreelancer,
    deductionsByFreelancer,
    dispatch,
    watch,
    calculatedAmounts,
    preferences,
    customerState,
    globalData,
    copiedInvoiceData,
    removedCopiedWorkingHours,
    isInitialMount
  ]);

  return {
    calculatedSubAmount,
    isCalculateInvoiceLoading,
    setCalculatedSubAmount
  };
};
